import { BoxError, fromBase64, fromBase64Url, lockBox, newBoxKey, openBox, toBase64, toBase64Url, } from "@gigsync/crypto";
import { TRANSFER_MAX_CHARS } from "@gigsync/protocol";
import { HttpError } from "../net/api.js";
import { Collection } from "../ports.js";
import { moveCode, readMoveCode } from "./code.js";
import { collectRecords, MOVE_FORMAT, packMove, readPackage, recordCount } from "./pack.js";
import { MoveError, MoveProblem } from "./types.js";
/** What a box of one owner's records is for, authenticated with it: opened for anyone else, it fails. */
export const moveContext = (owner) => `${MOVE_FORMAT}:${owner}`;
const Status = { notFound: 404, gone: 410, tooLarge: 413, tooMany: 429 };
const PROBLEM_BY_STATUS = {
    [Status.notFound]: MoveProblem.gone,
    [Status.gone]: MoveProblem.expired,
    [Status.tooLarge]: MoveProblem.too_big,
    [Status.tooMany]: MoveProblem.too_many,
};
/** The server's answer to a transfer call as a MoveError when it is one the owner should be told about. */
export function moveProblemOf(err) {
    if (!(err instanceof HttpError))
        return err;
    const problem = PROBLEM_BY_STATUS[err.status];
    return problem ? new MoveError(problem) : err;
}
const mapped = (err) => {
    throw moveProblemOf(err);
};
/**
 * The old phone's half: collects every record of the owner, seals them with a fresh key, uploads the box to a slot and
 * gives the text for the QR, which alone carries the key. `storage` is the owner's view of the phone. Throws MoveError.
 */
export async function offerMove(storage, api, o) {
    const records = await collectRecords(storage);
    const input = { owner: o.owner, now: o.now, records, ...(o.lang ? { lang: o.lang } : {}) };
    const { bytes, package: pkg } = packMove(input, o.maxChars ?? TRANSFER_MAX_CHARS);
    const key = newBoxKey();
    const ciphertext = toBase64(lockBox(key, bytes, moveContext(o.owner)));
    const slot = await api.uploadTransfer({ ciphertext }).catch(mapped);
    const unsent = (await storage.ids(Collection.outbox)).length;
    return {
        text: moveCode({ slot: slot.id, key: toBase64Url(key) }),
        expires_at: slot.expires_at,
        left_out: pkg.left_out,
        records: recordCount(pkg.records),
        unsent,
    };
}
/**
 * The new phone's first half: reads the scanned text, downloads the box (the slot is gone after this one read), opens
 * it with the key from the QR and checks it is this owner's. Nothing is written yet. Throws MoveError.
 */
export async function openMove(api, o) {
    const qr = readMoveCode(o.text);
    if (!qr)
        throw new MoveError(MoveProblem.not_move);
    const download = await api.downloadTransfer(qr.slot).catch(mapped);
    if (download.device === o.device)
        throw new MoveError(MoveProblem.same_phone);
    let bytes;
    try {
        bytes = openBox(fromBase64Url(qr.key), fromBase64(download.ciphertext), moveContext(o.owner));
    }
    catch (err) {
        if (err instanceof BoxError)
            throw new MoveError(MoveProblem.damaged);
        throw err;
    }
    return { package: readPackage(bytes, o.owner), from: download.device };
}
/**
 * Signs the old phone out for good, once the new one has written everything: it leaves the directory, so nobody seals
 * to it any more, and its session stops working. Already signed out counts as done, so a retry is safe.
 */
export async function revokeOldPhone(api, device) {
    try {
        await api.revokeDevice(device);
    }
    catch (err) {
        if (err instanceof HttpError && err.status === Status.notFound)
            return;
        throw err;
    }
}
//# sourceMappingURL=move.js.map